import { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  SortingState,
  flexRender,
  createColumnHelper,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Button } from '@/client/components/ui/button';
import { CacheTimestamp } from '@/client/components/cache-timestamp';
import { RefreshCw } from 'lucide-react';
import { API_BASE_URL } from '@/client/config';
import { SortableHeader } from '@/client/components/table-utils';
import { Badge } from '@/client/components/ui/badge';
import { Progress } from '@/client/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { formatDistanceToNow } from 'date-fns';

type ServerType = 'ingress' | 'egress' | 'transcode' | 'unknown';

interface DBServerInfo {
  serverType: ServerType;
  dbStatus?: string | null;
  timestamp?: string | null;
  sourceConnectionCount?: number | null;
  maxSources?: number | null;
  maxSlots?: number | null;
  freeSlots?: number | null;
  sysType?: string | null;
}

interface EC2Instance {
  instanceId: string;
  name: string | null;
  instanceType: string | null;
  state: string | null;
  launchTime: string | null;
  privateIp: string | null;
  publicIp: string | null;
  availabilityZone: string | null;
  serverType: ServerType;
  dbInfo: DBServerInfo | null;
}

interface EC2Metrics {
  instanceId: string;
  cpu?: number;
}

interface EC2InstanceWithMetrics extends EC2Instance {
  metrics?: EC2Metrics;
}

interface EC2Response {
  instances: EC2Instance[];
  cacheInfo?: { cachedAt: string };
}

interface MetricsResponse {
  metrics: EC2Metrics[];
  cacheInfo?: { cachedAt: string };
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'aws/ec2-health',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: EC2HealthComponent,
});

const columnHelper = createColumnHelper<EC2InstanceWithMetrics>();

function StateBadge({ state }: { state: string | null }) {
  if (!state) return <span className="text-muted-foreground">-</span>;

  const variant = state === 'running' ? 'default' :
                  state === 'stopped' ? 'secondary' :
                  ['terminated', 'shutting-down'].includes(state) ? 'destructive' :
                  'outline';

  return (
    <Badge variant={variant} className={state === 'running' ? 'bg-green-600' : ''}>
      {state}
    </Badge>
  );
}

function ServerTypeBadge({ type }: { type: ServerType }) {
  const colors: Record<ServerType, string> = {
    ingress: 'bg-blue-600',
    egress: 'bg-purple-600',
    transcode: 'bg-orange-600',
    unknown: 'bg-gray-500',
  };

  return (
    <Badge className={colors[type]}>
      {type}
    </Badge>
  );
}

function CPUCell({ cpu }: { cpu?: number }) {
  if (cpu === undefined) return <span className="text-muted-foreground">-</span>;

  const color = cpu > 80 ? 'bg-red-500' : cpu > 60 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <Progress value={cpu} className="h-2 w-12" indicatorClassName={color} />
      <span className="text-sm">{cpu}%</span>
    </div>
  );
}

// Simplified columns for main table
const columns = [
  columnHelper.accessor('instanceId', {
    header: ({ column }) => <SortableHeader column={column}>Instance ID</SortableHeader>,
    cell: (info) => {
      const id = info.getValue();
      const truncated = id.length > 12 ? `${id.slice(0, 10)}...` : id;
      return <span className="font-mono text-sm" title={id}>{truncated}</span>;
    },
  }),
  columnHelper.accessor('name', {
    header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
    cell: (info) => info.getValue() ?? <span className="text-muted-foreground italic">unnamed</span>,
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.name;
      const b = rowB.original.name;
      // Named instances first, then unnamed (nulls last)
      if (a === null && b === null) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      return a.localeCompare(b);
    },
  }),
  columnHelper.accessor('serverType', {
    header: ({ column }) => <SortableHeader column={column}>Type</SortableHeader>,
    cell: (info) => <ServerTypeBadge type={info.getValue()} />,
    filterFn: 'equals',
    sortingFn: (rowA, rowB) => {
      const order: Record<ServerType, number> = { ingress: 0, egress: 1, transcode: 2, unknown: 3 };
      return order[rowA.original.serverType] - order[rowB.original.serverType];
    },
  }),
  columnHelper.accessor('state', {
    header: ({ column }) => <SortableHeader column={column}>State</SortableHeader>,
    cell: (info) => <StateBadge state={info.getValue()} />,
    filterFn: 'equals',
  }),
  columnHelper.accessor((row) => row.metrics?.cpu, {
    id: 'cpu',
    header: ({ column }) => <SortableHeader column={column}>CPU</SortableHeader>,
    cell: (info) => <CPUCell cpu={info.getValue()} />,
  }),
  columnHelper.accessor('launchTime', {
    header: ({ column }) => <SortableHeader column={column}>Uptime</SortableHeader>,
    cell: (info) => {
      const launchTime = info.getValue();
      if (!launchTime) return <span className="text-muted-foreground">-</span>;
      return formatDistanceToNow(new Date(launchTime), { addSuffix: false });
    },
  }),
  columnHelper.accessor('instanceType', {
    header: ({ column }) => <SortableHeader column={column}>Instance Type</SortableHeader>,
    cell: (info) => <span className="font-mono text-sm">{info.getValue() ?? '-'}</span>,
  }),
];


function EC2HealthComponent() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [instances, setInstances] = useState<EC2Instance[]>([]);
  const [metrics, setMetrics] = useState<EC2Metrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cacheInfo, setCacheInfo] = useState<{ cachedAt: string } | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'serverType', desc: false }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [stateFilter, setStateFilter] = useState<string>('running');

  const fetchData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();

      if (forceRefresh) {
        await Promise.all([
          fetch(`${API_BASE_URL}/api/cache/invalidate`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: 'aws:ec2:instances' }),
          }),
          fetch(`${API_BASE_URL}/api/cache/invalidate`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: 'aws:ec2:metrics' }),
          }),
        ]);
      }

      const [instancesRes, metricsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/aws/ec2/instances`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/api/aws/ec2/metrics`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (!instancesRes.ok) {
        const err = await instancesRes.json();
        throw new Error(err.message || 'Failed to fetch EC2 instances');
      }

      const instancesData: EC2Response = await instancesRes.json();
      setInstances(instancesData.instances);
      setCacheInfo(instancesData.cacheInfo ?? null);

      if (metricsRes.ok) {
        const metricsData: MetricsResponse = await metricsRes.json();
        setMetrics(metricsData.metrics);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [getAccessTokenSilently]);

  // Apply filters
  useEffect(() => {
    const filters: ColumnFiltersState = [];
    if (typeFilter !== 'all') {
      filters.push({ id: 'serverType', value: typeFilter });
    }
    if (stateFilter !== 'all') {
      filters.push({ id: 'state', value: stateFilter });
    }
    setColumnFilters(filters);
  }, [typeFilter, stateFilter]);

  // Merge instances with metrics
  const instancesWithMetrics: EC2InstanceWithMetrics[] = useMemo(() => {
    const metricsMap = new Map(metrics.map((m) => [m.instanceId, m]));
    return instances.map((instance) => ({
      ...instance,
      metrics: metricsMap.get(instance.instanceId),
    }));
  }, [instances, metrics]);

  const table = useReactTable({
    data: instancesWithMetrics,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: { sorting, columnFilters },
  });

  // Count by type for summary
  const typeCounts = useMemo(() => {
    const counts = { ingress: 0, egress: 0, transcode: 0, unknown: 0, total: 0 };
    const running = instances.filter(i => i.state === 'running');
    running.forEach(i => {
      counts[i.serverType]++;
      counts.total++;
    });
    return counts;
  }, [instances]);

  return (
    <Layout>
      <div>
        <section>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-semibold">EC2 Health</h3>
            <CacheTimestamp cachedAt={cacheInfo?.cachedAt ?? null} />
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">Running Instances</div>
              <div className="text-2xl font-bold">{typeCounts.total}</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">Ingress</div>
              <div className="text-2xl font-bold text-blue-600">{typeCounts.ingress}</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">Egress</div>
              <div className="text-2xl font-bold text-purple-600">{typeCounts.egress}</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">Transcode</div>
              <div className="text-2xl font-bold text-orange-600">{typeCounts.transcode}</div>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded mt-4 mb-4">
              <p className="m-0 text-red-800 font-medium">Error: {error}</p>
            </div>
          )}

          <div className="flex justify-between items-center gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Server Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="ingress">Ingress</SelectItem>
                  <SelectItem value="egress">Egress</SelectItem>
                  <SelectItem value="transcode">Transcode</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
              <Select value={stateFilter} onValueChange={setStateFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="stopped">Stopped</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={() => fetchData(true)}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Force Refresh
            </Button>
          </div>

          {loading && instances.length === 0 && <p>Loading EC2 instances...</p>}

          {!loading && !error && instances.length === 0 && (
            <p>No EC2 instances found.</p>
          )}

          {instances.length > 0 && (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        onClick={() => navigate({ to: '/aws/ec2/$instanceId', params: { instanceId: row.original.instanceId } })}
                        className="cursor-pointer hover:bg-accent"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Showing {table.getRowModel().rows.length} of {instances.length} instances • Click row for details
              </p>
            </>
          )}
        </section>
      </div>
    </Layout>
  );
}
